import {
  AWSAuthenticationInterface,
  Localization,
  SessionContext,
  SettingsManager,
  ViamapPersistenceLayer,
} from "@viamap/viamap2-common";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormControl, ProgressBar } from "react-bootstrap";
import ReactDataGrid from "react-data-grid";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { AWSAPIGatewayInterface } from "src/managers/AWSAPIGatewayInterface";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { GlassCheckbox } from "./MitGlassComponents";
import { message } from "./Message";

type SharedMapLinkRow = {
  id: string;
  filePath: string;
  title: string;
  userName: string;
  userEmail: string;
  createDate: string;
  createDateSort: number;
  expiryDate: string;
  expiryDateSort: number;
  status: string;
  isExpired: boolean;
};

type LinkScope = "user" | "organization" | "all";

type ParsedLinkMetadata = {
  title: string;
  userName: string;
  userId: string;
  createDate: Date | undefined;
  expiryDate: Date | undefined;
};

type AccessibleLink = {
  filePath: string;
  meta: ParsedLinkMetadata;
  state?: any;
};

type PendingConfirmation =
  | { kind: "single"; row: SharedMapLinkRow }
  | { kind: "bulk"; rows: SharedMapLinkRow[]; message: string };

const mapOverlayPortalTarget = () => document.getElementById("Mit-MapOverlay") || document.body;

const overviewToolbarStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "8px",
  marginBottom: "12px",
};

const overviewToolbarRowStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap" as const,
  gap: "8px",
};

const overviewScopeLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "28px",
  marginLeft: "2px",
  opacity: 0.85,
  whiteSpace: "nowrap" as const,
};

const overviewDateGroupStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
};

const overviewDateLabelStyle = {
  opacity: 0.8,
  whiteSpace: "nowrap" as const,
};

const mapLinkGridCellCenterStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
} as const;

const mapLinkGridActionsStyle = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "100%",
} as const;

function metadataValue(metadata: Record<string, string> | undefined, key: string): string {
  if (!metadata) {
    return "";
  }
  return metadata[key] ?? metadata[key.toLowerCase()] ?? "";
}

function decodeMetadataValue(raw: string): string {
  if (!raw) {
    return "";
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function decodeMetadataTitle(raw: string): string {
  const decoded = decodeMetadataValue(raw);
  if (!decoded) {
    return Localization.getText("Savemap:defaultFileName");
  }
  return decoded;
}

function parseHeadMetadata(head: { Metadata?: Record<string, string>; LastModified?: Date }): ParsedLinkMetadata {
  const metadata = head.Metadata;
  const expirationRaw = decodeMetadataValue(metadataValue(metadata, "expirationDate"));
  return {
    title: decodeMetadataTitle(metadataValue(metadata, "mapTitle")),
    userName: decodeMetadataValue(metadataValue(metadata, "userName")),
    userId: decodeMetadataValue(metadataValue(metadata, "userId")),
    createDate: head.LastModified,
    expiryDate: expirationRaw ? new Date(expirationRaw) : undefined,
  };
}

const METADATA_TIMEOUT_MS = 15000;
const BLOB_TIMEOUT_MS = 60000;
const MAX_MAP_LINK_COUNT = 10000;

async function listMapLinkFilePaths(savedMapPath: string): Promise<string[]> {
  const bucket = SettingsManager.getSystemSetting("mapitS3Bucket");
  const filePaths: string[] = [];
  const params: {
    Bucket: string;
    MaxKeys: number;
    Prefix: string;
    Delimiter: string;
    ContinuationToken?: string;
  } = {
    Bucket: bucket,
    MaxKeys: 1000,
    Prefix: savedMapPath,
    Delimiter: savedMapPath,
  };

  return new Promise((resolve, reject) => {
    function listPage() {
      AWSAuthenticationInterface.getS3Instance().listObjectsV2(params, (err, data) => {
        if (err) {
          reject(err);
          return;
        }

        const contents = [...(data?.Contents ?? [])];
        if (contents.length && contents[0].Key?.endsWith("/")) {
          contents.shift();
        }

        for (const content of contents) {
          const key = content.Key;
          if (key && key !== savedMapPath && !key.endsWith("/")) {
            filePaths.push(key);
            if (filePaths.length >= MAX_MAP_LINK_COUNT) {
              resolve(filePaths);
              return;
            }
          }
        }

        if (data?.IsTruncated && filePaths.length < MAX_MAP_LINK_COUNT) {
          params.ContinuationToken = data.NextContinuationToken;
          listPage();
          return;
        }

        resolve(filePaths);
      });
    }

    listPage();
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function fetchLinkMetadata(filePath: string): Promise<ParsedLinkMetadata> {
  try {
    const persist = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("mapitS3Bucket"));
    const head = await withTimeout(persist.headObjectS3(filePath), METADATA_TIMEOUT_MS);
    return parseHeadMetadata(head);
  } catch {
    return {
      title: linkRefFromPath(filePath),
      userName: "",
      userId: "",
      createDate: undefined,
      expiryDate: undefined,
    };
  }
}

async function fetchLinkBlob(filePath: string): Promise<any | undefined> {
  try {
    return await withTimeout(AWSAPIGatewayInterface.loadStateS3(filePath), BLOB_TIMEOUT_MS);
  } catch {
    return undefined;
  }
}

function enrichFromBlob(headMeta: ParsedLinkMetadata, state: any): ParsedLinkMetadata {
  return {
    title: state?.mapInfo?.mapTitle || headMeta.title,
    userName: state?.userName || headMeta.userName,
    userId: state?.userId || headMeta.userId,
    createDate: state?.timeGenerated ? new Date(state.timeGenerated) : headMeta.createDate,
    expiryDate: state?.endDate ? new Date(state.endDate) : headMeta.expiryDate,
  };
}

function formatDate(date?: Date): string {
  if (!date || Number.isNaN(date.getTime())) {
    return Localization.getText("Unknown");
  }
  return date.toLocaleDateString(Localization.getLocale());
}

function linkStatus(expiryDate?: Date): string {
  if (!expiryDate || Number.isNaN(expiryDate.getTime())) {
    return Localization.getText("Unknown");
  }
  return expiryDate.getTime() > Date.now()
    ? Localization.getText("Active")
    : Localization.getText("Expired");
}

function linkRefFromPath(filePath: string): string {
  return filePath.split("/").at(-1) || filePath;
}

function shouldIncludeLink(
  meta: ParsedLinkMetadata,
  sessionUserRef: string,
  linkScope: LinkScope,
  customerRef: string,
): boolean {
  if (linkScope === "all") {
    return true;
  }
  const userId = meta.userId;
  if (!userId) {
    return false;
  }
  if (linkScope === "user") {
    return userId === sessionUserRef;
  }
  if (userId === sessionUserRef) {
    return true;
  }
  return ViamapPersistenceLayer.extractCustomerRef(userId) === customerRef;
}

function isExpiredRow(row: SharedMapLinkRow, cutoffTime = Date.now()): boolean {
  if (row.isExpired) {
    return row.expiryDateSort <= 0 || row.expiryDateSort < cutoffTime;
  }
  return false;
}

function dateInputToDate(value: string): Date | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function mapLinkMetadataFromState(state: any, expiryDate: Date): Record<string, string> {
  return {
    mapTitle: encodeURI(state?.mapInfo?.mapTitle || ""),
    expirationDate: expiryDate.toISOString(),
    userId: state?.userId || "",
    userName: state?.userName || "",
  };
}

async function updateMapLinkExpiry(filePath: string, expiryDate: Date): Promise<void> {
  const state = await AWSAPIGatewayInterface.loadStateS3(filePath);
  state.endDate = expiryDate;
  await AWSAPIGatewayInterface.persistStateS3(filePath, state, mapLinkMetadataFromState(state, expiryDate));
}

function toRow(filePath: string, meta: ParsedLinkMetadata): SharedMapLinkRow {
  const createDate = meta.createDate;
  const expiryDate = meta.expiryDate;
  const expiryDateSort = expiryDate?.getTime() || 0;
  const isExpired = Boolean(expiryDate && !Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() < Date.now());
  return {
    id: linkRefFromPath(filePath),
    filePath,
    title: meta.title,
    userName: meta.userName || Localization.getText("Unknown"),
    userEmail: meta.userId || Localization.getText("Unknown"),
    createDate: formatDate(createDate),
    createDateSort: createDate?.getTime() || 0,
    expiryDate: formatDate(expiryDate),
    expiryDateSort,
    status: linkStatus(expiryDate),
    isExpired,
  };
}

export const MaplinkOverview = (prop: { managedWindow: ManagedWindow; close: () => void }) => {
  const { state: sessionState } = useContext(SessionContext);
  const { hasAccessToFeature } = useContext(ApplicationStateContext);
  const isOrgAdmin = hasAccessToFeature(Feature.CustomerAdministrator);
  const isGlobalAdmin = hasAccessToFeature(Feature.GlobalAdministrator);
  const [linkScope, setLinkScope] = useState<LinkScope>("user");
  const showUserColumns = linkScope !== "user";

  const [rows, setRows] = useState<SharedMapLinkRow[]>([]);
  const [loadingCount, setLoadingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingPhase, setLoadingPhase] = useState<"scan" | "details">("scan");
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumns, setSortColumns] = useState<any[]>([]);
  const [expiryRow, setExpiryRow] = useState<SharedMapLinkRow | undefined>();
  const [expiryInput, setExpiryInput] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | undefined>();
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteExpiredBeforeInput, setDeleteExpiredBeforeInput] = useState("");
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState(320);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) {
      return;
    }

    const updateHeight = () => {
      const height = container.getBoundingClientRect().height;
      if (height > 0) {
        setGridHeight(Math.floor(height));
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (expiryRow?.expiryDateSort) {
      setExpiryInput(new Date(expiryRow.expiryDateSort).toISOString().slice(0, 10));
    } else if (expiryRow) {
      setExpiryInput("");
    }
  }, [expiryRow]);

  useEffect(() => {
    setLinkScope((current) => {
      if (current === "all" && !isGlobalAdmin) {
        return "user";
      }
      if (current === "organization" && !isOrgAdmin) {
        return "user";
      }
      return current;
    });
  }, [isOrgAdmin, isGlobalAdmin]);

  useEffect(() => {
    setSelectedRowIds((current) => {
      const rowIds = new Set(rows.map((row) => row.id));
      return new Set(Array.from(current).filter((id) => rowIds.has(id)));
    });
  }, [rows]);

  useEffect(() => {
    let cancelled = false;

    async function loadLinks() {
      setRows([]);
      setSelectedRowIds(new Set());
      setLoadingCount(0);
      setTotalCount(0);
      setIsLoading(true);

      const savedMapPath = SettingsManager.getSystemSetting("mapLinkOnS3Path", "savedmaps/");
      const filePaths = await listMapLinkFilePaths(savedMapPath);

      if (cancelled) {
        return;
      }

      const workers = 6;

      setLoadingPhase("scan");
      setTotalCount(filePaths.length);

      const accessibleLinks: AccessibleLink[] = [];
      let scanIndex = 0;
      let scanCompleted = 0;

      async function scanWorker() {
        while (true) {
          const currentIndex = scanIndex++;
          if (currentIndex >= filePaths.length) {
            return;
          }

          const filePath = filePaths[currentIndex];
          try {
            const meta = await fetchLinkMetadata(filePath);
            const state = !meta.userId && linkScope !== "all" ? await fetchLinkBlob(filePath) : undefined;
            const enrichedMeta = state ? enrichFromBlob(meta, state) : meta;
            if (
              !cancelled &&
              shouldIncludeLink(enrichedMeta, sessionState.userRef, linkScope, sessionState.customerRef)
            ) {
              accessibleLinks.push({ filePath, meta: enrichedMeta, state });
            }
          } finally {
            if (!cancelled) {
              scanCompleted++;
              setLoadingCount(scanCompleted);
            }
          }
        }
      }

      await Promise.all(Array.from({ length: workers }, () => scanWorker()));

      if (cancelled) {
        return;
      }

      // Phase 2: fetch full blob for accessible links to populate table details.
      setLoadingPhase("details");
      setLoadingCount(0);
      setTotalCount(accessibleLinks.length);

      const rowResults: (SharedMapLinkRow | undefined)[] = Array.from({ length: accessibleLinks.length });
      let detailIndex = 0;
      let detailCompleted = 0;

      async function detailWorker() {
        while (true) {
          const currentIndex = detailIndex++;
          if (currentIndex >= accessibleLinks.length) {
            return;
          }

          const { filePath, meta: headMeta, state: existingState } = accessibleLinks[currentIndex];
          try {
            const state = existingState ?? await fetchLinkBlob(filePath);
            const meta = state ? enrichFromBlob(headMeta, state) : headMeta;
            if (!cancelled) {
              rowResults[currentIndex] = toRow(filePath, meta);
              setRows(rowResults.filter((row): row is SharedMapLinkRow => row !== undefined));
            }
          } finally {
            if (!cancelled) {
              detailCompleted++;
              setLoadingCount(detailCompleted);
            }
          }
        }
      }

      await Promise.all(Array.from({ length: workers }, () => detailWorker()));

      if (!cancelled) {
        setIsLoading(false);
      }
    }

    loadLinks().catch((err) => {
      if (!cancelled) {
        setIsLoading(false);
      }
      message.error(err?.message || String(err));
    });

    return () => {
      cancelled = true;
    };
  }, [sessionState.userRef, sessionState.customerRef, linkScope, reloadToken]);

  const displayRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [rows, sortColumns]);

  const selectedRows = useMemo(() => {
    return rows.filter((row) => selectedRowIds.has(row.id));
  }, [rows, selectedRowIds]);

  const expiredRows = useMemo(() => {
    return rows.filter((row) => isExpiredRow(row));
  }, [rows]);

  const expiredBeforeRows = useMemo(() => {
    const cutoff = dateInputToDate(deleteExpiredBeforeInput);
    if (!cutoff) {
      return [];
    }
    return rows.filter((row) => isExpiredRow(row, cutoff.getTime()));
  }, [rows, deleteExpiredBeforeInput]);

  const columns = useMemo(() => {
    const allVisibleRowsSelected = displayRows.length > 0 && displayRows.every((row) => selectedRowIds.has(row.id));
    const toggleAllVisibleRows = () => {
      setSelectedRowIds((current) => {
        if (allVisibleRowsSelected) {
          const next = new Set(current);
          displayRows.forEach((row) => next.delete(row.id));
          return next;
        }
        return new Set([...Array.from(current), ...displayRows.map((row) => row.id)]);
      });
    };

    const baseColumns: any[] = [
      {
        key: "select-row",
        name: "",
        width: 42,
        minWidth: 42,
        maxWidth: 42,
        sortable: false,
        resizable: false,
        headerCellClass: "map-link-select-header",
        cellClass: "map-link-select-cell",
        renderHeaderCell: () => (
          <div
            style={mapLinkGridCellCenterStyle}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <GlassCheckbox
              checked={allVisibleRowsSelected}
              disabled={displayRows.length === 0}
              onClick={(event) => {
                event.stopPropagation();
                toggleAllVisibleRows();
              }}
            />
          </div>
        ),
        renderCell: ({ row }: { row: SharedMapLinkRow }) => (
          <div
            style={mapLinkGridCellCenterStyle}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <GlassCheckbox
              checked={selectedRowIds.has(row.id)}
              onClick={() => {
                setSelectedRowIds((current) => {
                  const next = new Set(current);
                  if (next.has(row.id)) {
                    next.delete(row.id);
                  } else {
                    next.add(row.id);
                  }
                  return next;
                });
              }}
            />
          </div>
        ),
      },
      { key: "title", name: Localization.getText("Title"), width: 220, sortable: true, resizable: true,
        renderCell: ({ row }: { row: SharedMapLinkRow }) => (
          <span className={isExpiredRow(row) ? "map-link-title-expired" : undefined}>{row.title}</span>
        ),
      },
      { key: "createDate", name: Localization.getText("Published date"), width: 130, sortable: true, resizable: false },
      {
        key: "expiryDate",
        name: Localization.getText("Expiry date"),
        width: 130,
        sortable: true,
        resizable: false,
        renderCell: ({ row }: { row: SharedMapLinkRow }) => (
          <span className={isExpiredRow(row) ? "map-link-expiry-date-expired" : undefined}>{row.expiryDate}</span>
        ),
      },
      {
        key: "status",
        name: Localization.getText("Status"),
        width: 90,
        sortable: true,
        resizable: false,
        renderCell: ({ row }: { row: SharedMapLinkRow }) => (
          <span className={isExpiredRow(row) ? "map-link-status-expired" : undefined}>
            {row.isExpired ? Localization.getText("Expired") : Localization.getText("Active")}
          </span>
        ),
      },
    ];

    if (showUserColumns) {
      baseColumns.push(
        { key: "userName", name: Localization.getText("User name"), width: 160, sortable: true, resizable: true },
        { key: "userEmail", name: Localization.getText("Email"), width: 220, sortable: true, resizable: true },
      );
    }

    const ActionFormatter = ({ row }: { row: SharedMapLinkRow }) => (
      <div style={mapLinkGridActionsStyle} className="map-link-actions-row">
        <GlassButtonRow wrap>
          <GlassButton
            onClick={() => {
              window.open(
                `${window.location.protocol}//${window.location.host}${window.location.pathname}?mapdef=${row.id}`,
                "_blank",
              );
            }}
          >
            {Localization.getText("Open")}
          </GlassButton>
          <GlassButton onClick={() => setExpiryRow(row)}>
            {Localization.getText("Change expiry")}
          </GlassButton>
          <GlassButton onClick={() => setPendingConfirmation({ kind: "single", row })}>
            {Localization.getText("Delete")}
          </GlassButton>
        </GlassButtonRow>
      </div>
    );

    return [
      ...baseColumns,
      {
        key: "actions",
        name: Localization.getText("MapLink:Actions"),
        width: showUserColumns ? 300 : 280,
        sortable: false,
        resizable: false,
        cellClass: "map-link-actions-cell",
        renderCell: ActionFormatter,
      },
    ];
  }, [displayRows, selectedRowIds, showUserColumns, sessionState.languageCode]);

  async function saveExpiry() {
    if (!expiryRow || !expiryInput) {
      return;
    }
    setSavingExpiry(true);
    try {
      const newDate = dateInputToDate(expiryInput);
      if (!newDate) {
        throw new Error(Localization.getText("Invalid date"));
      }
      await updateMapLinkExpiry(expiryRow.filePath, newDate);
      message.info(Localization.getText("Map link expiry updated"));
      setReloadToken((value) => value + 1);
      setExpiryRow(undefined);
    } catch (err: any) {
      message.error(err?.message || String(err));
    } finally {
      setSavingExpiry(false);
    }
  }

  async function confirmPendingDelete() {
    if (!pendingConfirmation || bulkDeleting) {
      return;
    }

    if (pendingConfirmation.kind === "single") {
      const row = pendingConfirmation.row;
      setPendingConfirmation(undefined);
      try {
        await AWSAPIGatewayInterface.deleteFileS3(row.filePath);
        setRows((current) => current.filter((currentRow) => currentRow.id !== row.id));
        message.info(Localization.getText("Deleted"));
      } catch (err: any) {
        message.error(err?.message || String(err));
      }
      return;
    }

    setBulkDeleting(true);
    const rowsToDelete = pendingConfirmation.rows;
    setPendingConfirmation(undefined);
    const deletedIds = new Set<string>();
    const failures: string[] = [];

    for (const row of rowsToDelete) {
      try {
        await AWSAPIGatewayInterface.deleteFileS3(row.filePath);
        deletedIds.add(row.id);
      } catch (err: any) {
        failures.push(`${row.title}: ${err?.message || String(err)}`);
      }
    }

    setRows((current) => current.filter((row) => !deletedIds.has(row.id)));
    setSelectedRowIds((current) => new Set(Array.from(current).filter((id) => !deletedIds.has(id))));

    if (failures.length > 0) {
      message.error(Localization.getFormattedText("Deleted {deleted} links. {failed} failed.", {
        deleted: deletedIds.size,
        failed: failures.length,
      }));
    } else {
      message.info(Localization.getFormattedText("{count} map links deleted", { count: deletedIds.size }));
    }

    setBulkDeleting(false);
  }

  function requestBulkDelete(rowsToDelete: SharedMapLinkRow[], confirmationMessage: string) {
    if (rowsToDelete.length === 0) {
      return;
    }
    setPendingConfirmation({ kind: "bulk", rows: rowsToDelete, message: confirmationMessage });
  }

  return (
    <AdvancedDragModal
      className="MapLinkOverview"
      window={prop.managedWindow}
      title={Localization.getText("Map Link Overview")}
      onClose={prop.close}
      variant={"NSDark"}
      PosDefault={{ top: "40px", left: "50%", width: "960px", height: "calc(100% - 80px)", transform: "translateX(-50%)" }}
      saveKey="MapLinkOverview"
      PositionToSave={{ width: true, height: true, top: true, left: true }}
      allowFullscreen
      allowUserMiniMize
    >
      <ADModalBody>
        <div className="map-link-overview-body">
        <div className="map-link-overview-header" style={overviewToolbarStyle}>
          <div style={overviewToolbarRowStyle}>
            <GlassButton onClick={() => setReloadToken((value) => value + 1)} disabled={isLoading}>
              {Localization.getText("Refresh")}
            </GlassButton>
            {isOrgAdmin ? (
              <GlassButton
                onClick={() => setLinkScope((current) => current === "organization" ? "user" : "organization")}
                disabled={isLoading}
              >
                {linkScope === "organization"
                  ? Localization.getText("Show my links")
                  : Localization.getText("Fetch organization links")}
              </GlassButton>
            ) : null}
            {isGlobalAdmin ? (
              <GlassButton
                onClick={() => setLinkScope((current) => current === "all" ? "user" : "all")}
                disabled={isLoading}
              >
                {linkScope === "all"
                  ? Localization.getText("Show my links")
                  : Localization.getText("Fetch all links")}
              </GlassButton>
            ) : null}
            <span style={overviewScopeLabelStyle}>
              {linkScope === "all"
                ? Localization.getText("Showing all links")
                : linkScope === "organization"
                  ? Localization.getText("Showing organization links")
                  : Localization.getText("Showing my links")}
              {` (${rows.length})`}
            </span>
          </div>

          <div style={overviewToolbarRowStyle}>
            <GlassButton
              disabled={selectedRows.length === 0 || bulkDeleting}
              onClick={() =>
                requestBulkDelete(
                  selectedRows,
                  Localization.getFormattedText("Delete {count} selected map links?", { count: selectedRows.length }),
                )}
            >
              {Localization.getFormattedText("Delete selected ({count})", { count: selectedRows.length })}
            </GlassButton>
            <GlassButton
              disabled={expiredRows.length === 0 || bulkDeleting}
              onClick={() =>
                requestBulkDelete(
                  expiredRows,
                  Localization.getFormattedText("Delete all {count} expired map links?", { count: expiredRows.length }),
                )}
            >
              {Localization.getFormattedText("Delete all expired ({count})", { count: expiredRows.length })}
            </GlassButton>
            <span style={overviewDateGroupStyle}>
              <span style={overviewDateLabelStyle}>{Localization.getText("Expired before")}</span>
              <FormControl
                type="date"
                size="sm"
                style={{ width: "150px", minHeight: "28px" }}
                value={deleteExpiredBeforeInput}
                onChange={(e) => setDeleteExpiredBeforeInput(e.target.value)}
              />
            </span>
            <GlassButton
              disabled={expiredBeforeRows.length === 0 || bulkDeleting}
              onClick={() =>
                requestBulkDelete(
                  expiredBeforeRows,
                  Localization.getFormattedText("Delete {count} map links expired before the selected date?", {
                    count: expiredBeforeRows.length,
                  }),
                )}
            >
              {Localization.getFormattedText("Delete expired before date ({count})", { count: expiredBeforeRows.length })}
            </GlassButton>
          </div>

        {isLoading && totalCount > 0 ? (
          <ProgressBar
            animated
            striped
            now={Math.round((loadingCount / totalCount) * 100)}
            label={
              isGlobalAdmin
                ? loadingPhase === "scan"
                  ? Localization.getFormattedText("Scanning shared links {done} / {total}", {
                      done: loadingCount,
                      total: totalCount,
                    })
                  : Localization.getFormattedText("Loading link details {done} / {total}", {
                      done: loadingCount,
                      total: totalCount,
                    })
                : undefined
            }
          />
        ) : null}
        </div>

        <div className="map-link-grid-container" ref={gridContainerRef}>
          <ReactDataGrid
          style={{ height: gridHeight, width: "100%", borderRadius: "4px" }}
          className="rdg-light map-link-overview-grid"
          columns={columns}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns as any}
          rows={displayRows}
          rowKeyGetter={(row) => row.id}
          rowHeight={36}
          enableVirtualization
          rowClass={(row) => (isExpiredRow(row) ? "map-link-row-expired" : "")}
          />
        </div>
        </div>

        {expiryRow
        ? createPortal(
          <AdvancedDragModal
            variant={"NSDark"}
            PosDefault={{ top: "40%", width: "450px", left: "50%", transform: "translate(-50%, -50%)" }}
            title={Localization.getText("Change map link expiry")}
            onClose={() => setExpiryRow(undefined)}
          >
            <ADModalBody>
              <p>{expiryRow.title}</p>
              <FormControl
                type="date"
                value={expiryInput}
                onChange={(e) => setExpiryInput(e.target.value)}
              />
            </ADModalBody>
            <ADModalFooter>
              <GlassButton onClick={() => setExpiryRow(undefined)}>{Localization.getText("Cancel")}</GlassButton>
              <GlassButton onClick={saveExpiry} disabled={savingExpiry || !expiryInput}>
                {Localization.getText("Save")}
              </GlassButton>
            </ADModalFooter>
          </AdvancedDragModal>,
          mapOverlayPortalTarget(),
        )
        : null}

      {pendingConfirmation
        ? createPortal(
          <ConfirmationDialog
            showWindow={1}
            header="Delete"
            onCancel={() => !bulkDeleting && setPendingConfirmation(undefined)}
            onSubmit={() => {
              confirmPendingDelete();
            }}
          >
            {pendingConfirmation.kind === "single"
              ? Localization.getFormattedText("Delete map link {title}?", {
                title: pendingConfirmation.row.title,
              })
              : pendingConfirmation.message}
          </ConfirmationDialog>,
          mapOverlayPortalTarget(),
        )
          : null}
      </ADModalBody>
    </AdvancedDragModal>
  );
};

type Comparator<T> = (a: T, b: T) => number;

function getComparator(sortColumn: string): Comparator<SharedMapLinkRow> {
  switch (sortColumn) {
    case "title":
    case "userName":
    case "userEmail":
      return (a, b) => (a[sortColumn] || "").localeCompare(b[sortColumn] || "");
    case "status":
      return (a, b) => Number(a.isExpired) - Number(b.isExpired);
    case "createDate":
    case "expiryDate":
      return (a, b) => (a[`${sortColumn}Sort` as keyof SharedMapLinkRow] as number) - (b[`${sortColumn}Sort` as keyof SharedMapLinkRow] as number);
    default:
      return () => 0;
  }
}
